/**
 * Cut-level stripe reveal.
 *
 * Sweeps the coloured protection-level bar under each product card from left to
 * right as it scrolls into view.
 *
 * Originally lived inline in products-list and products-list-no-filter. Lifted
 * out so every section that renders `product-item` gets it (collection grid,
 * search, recommendations, shop-the-look, collection panels), and so the
 * observer is created once rather than per section.
 */
(function () {
  'use strict';

  var SELECTOR = '.product-item__cut-level';
  var BOUND = 'cutLevelBound';

  // Honour the OS setting: leave the bar in its natural state, never animate.
  // The CSS guard covers the same case, this just avoids pointless observing.
  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var observer = null;

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    return observer;
  }

  function register(el) {
    if (el.dataset[BOUND]) return;
    el.dataset[BOUND] = '1';
    if (prefersReducedMotion) return;

    el.classList.add('animate-on-scroll');

    var obs = getObserver();
    if (obs) obs.observe(el);
    else el.classList.add('is-visible'); // no IntersectionObserver: show it
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(register);
  }

  // Tab panels are display:none until clicked, so their strips never get valid
  // geometry for the observer to fire on. Reveal them on tab switch instead.
  // Delegated so it also covers tabs rendered after load.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.collection-tabs_tab button');
    if (!button) return;
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    panel.querySelectorAll(SELECTOR).forEach(function (el) {
      el.classList.add('is-visible');
    });
  });

  // Catch cards injected after load: facet filtering, load more, quick view.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(SELECTOR)) register(node);
          else if (node.querySelectorAll) scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  // The collection grid re-renders via AJAX on filter/sort (component-facets.js).
  // The MutationObserver above already catches this, but the theme fires an
  // explicit event for it, so honour it rather than depend on a side effect.
  document.addEventListener('krown:main-collection-grid:filtered', function (event) {
    scan(event.detail && event.detail.gridElement);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }
})();
